'use client';

import React from 'react';

import {useFeatures} from '@udemy-v2/react-experimentation';

import {SHOW_TRENDING_SKILLS_ABOVE_AI_ERA_CAREER_BANNER} from '@/app/lib/experimentation';

import type {TrendingSkillsVariant} from '../data/types';
import {TrendingSkills, TrendingSkillsLoadingSkeleton} from './trending-skills/trending-skills';

interface TrendingSkillsTargeting {
    variant: TrendingSkillsVariant;
    isActive: boolean;
    isLoading: boolean;
}

/**
 * The experiment is targeted server-side to India/Japan traffic only via the
 * `enabled_for_in`/`enabled_for_japan` configuration keys on the flag.
 * Japan wins if both keys are somehow present.
 */
const useTrendingSkillsTargeting = (): TrendingSkillsTargeting => {
    const {featureVariantAssignments, isLoading} = useFeatures([
        SHOW_TRENDING_SKILLS_ABOVE_AI_ERA_CAREER_BANNER,
    ]);
    const configuration = featureVariantAssignments.get(
        SHOW_TRENDING_SKILLS_ABOVE_AI_ERA_CAREER_BANNER,
    )?.configuration;

    const isIndia = Boolean(configuration?.enabled_for_in);
    const isJapan = Boolean(configuration?.enabled_for_japan);
    const variant: TrendingSkillsVariant = isJapan ? 'japan' : isIndia ? 'india' : 'default';

    return {variant, isActive: variant !== 'default', isLoading};
};

type TrendingSkillsSlotName = 'above-ai-career-banner' | 'default';

/**
 * lohp-page.tsx mounts one instance per slot at two fixed JSX positions (above the
 * AI Career Banner, and after Discovery Units). Each instance independently asks
 * the shared targeting hook whether it's the active slot — only one ever renders
 * <TrendingSkills>, the other returns null.
 */
const TrendingSkillsSlot: React.FC<{slot: TrendingSkillsSlotName}> = ({slot}) => {
    const {variant, isActive, isLoading} = useTrendingSkillsTargeting();

    if (isLoading) {
        if (slot === 'default') {
            return <TrendingSkillsLoadingSkeleton />;
        }
        return null;
    }

    const isThisSlotActive = slot === 'above-ai-career-banner' ? isActive : !isActive;

    if (!isThisSlotActive) return null;

    return <TrendingSkills variant={variant} />;
};

/** Renders <TrendingSkills> above the AI Career Banner when the experiment is on. */
export const TrendingSkillsAboveAICareerBanner: React.FC = () => (
    <TrendingSkillsSlot slot="above-ai-career-banner" />
);

/** Renders <TrendingSkills> in its default position (section 11) when the experiment is off. */
export const TrendingSkillsDefaultPosition: React.FC = () => <TrendingSkillsSlot slot="default" />;
